import React, { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import { useAuth } from "../../../firebase/AuthProvider";
import { useLocation, useNavigate } from "react-router-dom";
import ActionSubType from "../../../interfaces/task/taskActions/ActionSubType";
import {
    getTaskActionSubTypes,
    publishTaskActionTemplate,
    updateTaskActionTemplate,
} from "../../../services/taskServices/TaskActionService";
import NewTaskActionType from "../../../interfaces/task/taskActions/NewTaskActionType";
import TemplateFile from "../../../interfaces/task/TaskFiles/TemplateFile";
import { getTemplateFiles } from "../../../services/fileServices/FileService";
import UpdateTaskActionType from "../../../interfaces/task/taskActions/UpdateTaskActionType";
import backarrowbtn from "../../../assets/images/backarrowbtn.svg";
import { Button } from "react-bootstrap";

interface OptionType {
    value: string;
    label: string;
}

interface EditTaskActivityTypeProps {
    taskActivity: ActionSubType;
    reload: () => void;
    back: () => void;
}

const EditTaskActivityType: React.FC<EditTaskActivityTypeProps> = ({
    taskActivity,
    reload,
    back,
}) => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [actionTypes, setActionTypes] = useState<ActionSubType[] | null>(null);
    const [actionTypeOptions, setActionTypeOptions] = useState<OptionType[]>([]);
    const [taskId, setTaskId] = useState<string>("");
    const [fileTemplates, setFileTemplates] = useState<TemplateFile[] | null>();
    const [formattedFileTemplates, setFormattedFileTemplates] = useState<
        OptionType[]
    >([]);
    const [loading, setLoading] = useState<boolean>(false);

    const labelsToFilterOut = [
        "COMMENT",
        "TASK FILE DELETED",
        "FILE UPLOAD",
        "TASK APPROVAL",
        "TASK SUBMISSION",
        "TASK COMPLETED",
    ];

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const taskId = searchParams.get("taskId");
        if (taskId !== null) setTaskId(taskId);
    }, [location]);

    useEffect(() => {
        if (!actionTypes && currentUser) {
            const getActionTypes = async () => {
                const authToken = await currentUser.getIdToken();
                const retrievedTypes = await getTaskActionSubTypes(authToken);
                if (retrievedTypes) {
                    const filteredOptions = retrievedTypes.filter(
                        (option) => !labelsToFilterOut.includes(option.actionSubTypeName)
                    );
                    setActionTypes(filteredOptions);
                }
            };
            getActionTypes();
        }
        if (currentUser !== null) {
            const fetchTemplateFiles = async () => {
                const authToken: string = await currentUser.getIdToken();
                const fetchedTemplateFiles = await getTemplateFiles(authToken);
                setFileTemplates(fetchedTemplateFiles);
            };

            fetchTemplateFiles();
        }
    }, [currentUser]);

    useEffect(() => {
        if (taskId) {
            setNewAction((prev) => ({
                ...prev,
                taskId: taskId.split("-")[1], // Update taskId in newAction when taskId changes
            }));
        }
    }, [taskId]);

    useEffect(() => {
        if (fileTemplates) {
            const formattedFileTemplates = fileTemplates.map((file) => ({
                value: file.id.toString(),
                label: file.fileName,
            }));
            if (formattedFileTemplates) {
                const updatedFileTemplates = formattedFileTemplates.concat({
                    value: "0",
                    label: "None",
                });
                setFormattedFileTemplates(updatedFileTemplates);
            }
        }
    }, [fileTemplates]);

    const [newAction, setNewAction] = useState<UpdateTaskActionType>({
        id: taskActivity.id.toString(),
        activityName: taskActivity.actionSubTypeName,
        description: taskActivity.description ? taskActivity.description : "",
        actionTypeId: "",
        actionSubTypeId: "",
        estimated_ttc: taskActivity.recommendedTimeInMinutes
            ? taskActivity.recommendedTimeInMinutes.toString()
            : "",
        requiresFile: taskActivity.requiresFile
            ? taskActivity.requiresFile.toString()
            : "",
    });

    useEffect(() => {
        if (actionTypes && actionTypes.length > 0) {
            const formattedActionTypes = actionTypes.map((at) => ({
                value: at.id.toString(),
                label: at.actionSubTypeName,
            }));
            setActionTypeOptions(formattedActionTypes);
        }
    }, [actionTypes]); // React only when actionTypes changes

    const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let { value } = e.target;
        setNewAction({ ...newAction, estimated_ttc: value.toString() });
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setNewAction({ ...newAction, [name]: value });
    };

    const validateFields = (): boolean => {
        // Check if any required fields are empty
        if (
            !newAction.description ||
            !newAction.estimated_ttc ||
            !newAction.activityName
        ) {
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateFields()) {
            console.error("Please fill out all required fields.");
            return;
        }
        if (currentUser) {
            setLoading(true); // Start loading
            const authToken: string = await currentUser.getIdToken();
            await updateTaskActionTemplate(authToken, newAction)
                .then((data) => {
                    reload()
                    back();
                })
                .catch((error) => {
                    console.error("Error:", error);
                })
                .finally(() => {
                    setLoading(false); // End loading
                });
        }
    };

    const handleActionRequiresFileChange = (
        selectedOption: SingleValue<OptionType>
    ) => {
        const requiresFile = selectedOption ? selectedOption.value : "";
        setNewAction({ ...newAction, requiresFile });
    };

    return (
        <>
            <div className="common_card">
                <div className="top border_tittle">
                    <div className="top_bar_tittle">
                        <h2>
                            <button onClick={back}>
                                {/* {"< "}Back{" "} */}
                                <img style={{ width: '1.5rem' }} src={backarrowbtn} alt="backarrowbtn" className="icon_sm" />
                            </button>
                            New Activity Type</h2>
                    </div>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="custom_input_flled">
                                <label htmlFor="description">Activity Name</label>
                                <textarea
                                    id="activityName"
                                    name="activityName"
                                    placeholder="A name for the activity..."
                                    value={newAction.activityName}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="custom_input_flled">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    placeholder="A brief description of what the task entails..."
                                    value={newAction.description}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="custom_input_flled">
                                <label htmlFor="durationInMinutes" >Duration (in minutes)</label>
                                <input
                                    type="number"
                                    id="durationInMinutes"
                                    name="durationInMinutes"
                                    value={newAction.estimated_ttc}
                                    onChange={handleDurationChange}
                                    min="0"
                                    step="15"
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="custom_input_flled mb-0">
                                <label htmlFor="durationInMinutes">
                                    Required File{" "}
                                    <span>(Optional)</span>:
                                </label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    options={formattedFileTemplates}
                                    onChange={handleActionRequiresFileChange}
                                    value={formattedFileTemplates.find(
                                        (option) => option.value === newAction.requiresFile
                                    )}
                                />{" "}
                            </div>
                        </div>
                    </div>
                    <div className="form_btn_flx mt-3">
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? "Updating..." : "Update Activity"}
                        </Button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default EditTaskActivityType;
